import { useState } from "react";
import { H3, Label } from "../Text";
import { toast } from "react-toastify";
import Circle from "../Animation/Circle";
import { Button } from "../Buttons";
import { Request } from "../../hooks/Request";
import TableComplete from "../Tables/TableComplete";
import { requestAuth } from "../services/RequestService";
import { useDialog } from "../../contexts/DialogContext";
import { DialogConfirmation } from "../Modal";
import CreatableSelect from "react-select/creatable";
import { validateArray } from "../../utils/validate";
import * as XLSX from "xlsx";

const DeleteAllConfirmation = ({ items, onClose, reload }) => {
  const [deleting, setDeleting] = useState(false);

  async function handleDeleteAll() {
    setDeleting(true);

    const results = [];
    const batchSize = 10;

    for (let index = 0; index < items.length; index += batchSize) {
      const batch = items.slice(index, index + batchSize);
      const batchResults = await Promise.allSettled(
        batch.map((item) =>
          requestAuth("delete", `/dataSet/procedure/subData/${item.id}`),
        ),
      );
      results.push(...batchResults);
    }

    const failedCount = results.filter(
      (result) => result.status === "rejected",
    ).length;
    const deletedCount = results.length - failedCount;

    await reload();
    onClose();

    if (failedCount === 0) {
      toast.success(`Se eliminaron ${deletedCount} ítems`);
      return;
    }

    toast.warning(
      `Se eliminaron ${deletedCount} ítems y ${failedCount} no pudieron eliminarse`,
    );
  }

  return (
    <div>
      <H3 className="text-center p-4">
        ¿Estás seguro de que quieres eliminar los {items.length} ítems de esta
        lista? Esta acción no se puede deshacer.
      </H3>
      <div className="flex gap-4 m-4">
        <Button
          onClick={onClose}
          disabled={deleting}
          className="w-full bg-[#fff] text-[#FBCE00] border-2 border-[#FBCE00]"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleDeleteAll}
          disabled={deleting}
          className="w-full"
        >
          {deleting ? "Eliminando..." : "Vaciar ítems"}
        </Button>
      </div>
    </div>
  );
};

const InputSubData = ({ field }) => {
  const { openDialog, dialogClose } = useDialog();

  const { data, loading, call } = Request({
    urlApi: `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
  });

  const {
    data: document,
    loading: loadDocument,
    call: callDocument,
  } = Request({
    urlApi: field.urlApi,
  });

  function handleImportExcel() {
    openDialog(
      <FormImportExcel
        field={field}
        reload={call}
        onClose={dialogClose}
        callDocument={callDocument}
        options={document}
        existingData={data}
      />,
    );
  }

  function handleForm(item) {
    const values = item.description2
      ? JSON.parse(item.description2) || null
      : null;
    openDialog(
      <>
        {loadDocument && (
          <FormAddDocument
            data={document}
            item={{
              ...item,
              desc: values?.desc || "",
              amount: values?.amount || "",
              unit: values?.unit || "",
              tr4: values?.tr4 || "",
              providerCode: values?.providerCode || "",
              description2: values?.Description2 || "",
            }}
            reload={call}
            field={field}
            onClose={dialogClose}
            callDocument={callDocument}
          />
        )}
        <br />
      </>,
    );
  }

  function handleDelete(data) {
    openDialog(
      <DialogConfirmation
        children={<H3>¿Estas seguro que quieres eliminar este item?</H3>}
        onClose={dialogClose}
        method="delete"
        url={`/dataSet/procedure/subData/${data.id}`}
        texBtn="Eliminar"
        handleSuccess={call}
      />,
    );
  }

  function handleDeleteAll() {
    openDialog(
      <DeleteAllConfirmation
        items={data}
        onClose={dialogClose}
        reload={call}
      />,
    );
  }

  return (
    <div className="bg-gray-300/40 p-2 rounded-md">
      <div className="flex justify-between items-center">
        <Label htmlFor={field.name} className={`${field.className} my-0`}>
          {field.labelCustom ? field.labelCustom : field.label}
        </Label>
        <div className="flex items-center gap-2">
          {field.name === "descripciónDelPedido" &&
            !field.lockEdition &&
            validateArray(data) && (
              <Button
                onClick={handleDeleteAll}
                className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700"
              >
                <i className="fas fa-trash mr-2"></i> Vaciar ítems
              </Button>
            )}
          <Button
            onClick={handleImportExcel}
            className="px-3 py-1 text-sm bg-green-600 hover:bg-green-700"
          >
            <i className="fas fa-file-excel mr-2"></i> Importar Excel
          </Button>
        </div>
      </div>

      <div className="bg-gray-100 rounded-lg p-3 pt-2">
        {loadDocument && (
          <FormAddDocument
            data={document}
            reload={call}
            field={field}
            onClose={() => { }}
            callDocument={callDocument}
          />
        )}
      </div>
      <br />
      {!loading ? (
        <Circle />
      ) : (
        <div className="w-full">
          <TableComplete
            header={[
              {
                name: "index",
                label: "#",
              },
              {
                name: "label",
                label: "Detalle",
                type: "custom",
                builder: (value, _, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2) || null
                    : null;
                  return (
                    <div className="">
                      <h3>{value}</h3>
                      <h3 className="font-normal">{values?.desc || "-"}</h3>
                    </div>
                  );
                },
              },
              {
                name: "description",
                label: "Unidad",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2) || null
                    : null;
                  return (
                    <div className="">
                      <h3 className="font-normal">{values?.unit || "-"}</h3>
                    </div>
                  );
                },
              },
              {
                name: "description",
                label: "Cantidad",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2) || null
                    : null;
                  return (
                    <div className="">
                      <h3 className="font-normal">{values?.amount || "-"}</h3>
                    </div>
                  );
                },
              },
              {
                name: "tr4",
                label: "Codigos",
                type: "custom",
                builder: (_, __, item) => {
                  const values = item.description2
                    ? JSON.parse(item.description2) || null
                    : null;
                  return (
                    <div className="">
                      <h3 className="font-normal">
                        <span className="font-bold">TR4:</span>{" "}
                        {values?.tr4 || "-"}
                      </h3>
                    </div>
                  );
                },
              },
              {
                name: "add",
                type: "action",
                label: "Opciones",
                sticky: true,
                actions: [
                  {
                    label: "Editar",
                    icon: "fas  fa-edit",
                    action: handleForm,
                    color: "text-[#1d4ed8]",
                  },
                  {
                    label: "Eliminar",
                    icon: "fas fa-trash",
                    action: handleDelete,
                    color: "text-red-500",
                  },
                ],
              },
            ]}
            data={data?.map((item, i) => ({ ...item, index: i + 1 }))}
          />
        </div>
      )}
    </div>
  );
};

const FormAddDocument = (props) => {
  const { data, item = null, reload, field, onClose, callDocument } = props;
  const [values, setValues] = useState(() => {
    const selectedOption = data?.find((opt) => opt.name === item?.label);
    let tr4FromOption = "";
    let hasTr4Inside = false;
    if (selectedOption && selectedOption.data) {
      try {
        const optionData = JSON.parse(selectedOption.data);
        if (optionData.tr4) {
          tr4FromOption = optionData.tr4;
          hasTr4Inside = true;
        }
      } catch (err) { }
    }

    return {
      Label: item?.label ? item.label : "",
      NumberValue: item?.numberValue ? item.numberValue : 0,
      Description: item?.description ? item.description : "",
      Description2: item?.description2 ? item.description2 : "",
      unit: item?.unit ? item.unit : "",
      amount: item?.amount ? item.amount : "",
      desc: item?.desc ? item.desc : "",
      tr4: hasTr4Inside ? tr4FromOption : (item?.tr4 ? item.tr4 : ""),
      providerCode: item?.providerCode ? item.providerCode : "",
      isNew: false,
      optionId: selectedOption?.id || null,
      hasTr4InOption: hasTr4Inside,
    };
  });

  async function handleSubmit() {
    if (values.isNew) {
      await handleCreateOption(values);
    } else if (values.optionId && !values.hasTr4InOption && values.tr4) {
      await handleUpdateOption(values);
    }
    const desc2 = {
      unit: values.unit || 0,
      amount: values.amount || 0,
      desc: values.desc || 0,
      tr4: values.tr4 || "",
      providerCode: values.providerCode || "",
    };
    await requestAuth(
      item ? "put" : "post",
      item
        ? `/dataSet/procedure/subData/${item.id}`
        : `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
      { ...values, Description2: JSON.stringify(desc2) },
    )
      .then(() => {
        reload();
        onClose();
        setValues({
          ...values,
          NumberValue: 0,
          Description: "",
          unit: "",
          amount: "",
          desc: "",
          tr4: "",
          providerCode: "",
        });
        toast.success("Registro exitoso");
      })
      .catch(() => {
        toast.error("Se produjo un error al guardar");
      });
  }

  async function handleCreateOption(value) {
    await requestAuth("post", "/Dropdown/Option", {
      Name: value.Label,
      DropdownListLabel: field.urlInitial,
      data: JSON.stringify({ tr4: value.tr4 || "" }),
    }).then(() => {
      callDocument();
    });
  }

  async function handleUpdateOption(value) {
    await requestAuth("put", `/Dropdown/Option/${value.optionId}`, {
      Name: value.Label,
      DropdownListLabel: field.urlInitial,
      data: JSON.stringify({ tr4: value.tr4 || "" }),
    }).then(() => {
      callDocument();
    });
  }

  function handleChangeInp(e) {
    const { name, value } = e.target;
    setValues((a) => ({
      ...a,
      [name]: value,
    }));
  }
  function handleChange(e) {
    const selectedOption = data?.find((opt) => opt.name === e?.value);
    let tr4Value = "";
    let hasTr4 = false;

    if (selectedOption && selectedOption.data) {
      try {
        const optionData = JSON.parse(selectedOption.data);
        if (optionData.tr4) {
          tr4Value = optionData.tr4;
          hasTr4 = true;
        }
      } catch (err) {
        console.error("Error parsing option data", err);
      }
    }

    setValues((a) => ({
      ...a,
      Label: e?.value || "",
      isNew: e?.__isNew__ || false,
      tr4: hasTr4 ? tr4Value : (e?.__isNew__ ? a.tr4 : ""),
      optionId: selectedOption?.id || null,
      hasTr4InOption: hasTr4,
    }));
  }

  return (
    <form className="flex flex-col">
      <Label className={`mt-2`}>Detalle</Label>
      {validateArray(data) ? (
        <CreatableSelect
          isSearchable={true}
          onChange={handleChange}
          styles={{
            placeholder: (base) => ({
              ...base,
              color: "black",
            }),
          }}
          defaultInputValue={values.Label}
          placeholder={"Seleccionar..."}
          isOptionSelected={false}
          isClearable
          // isLoading={field.urlApi ? !loading : false}
          options={data.map((option) => {
            let tr4 = "";
            if (option.data) {
              try {
                const parsedData = JSON.parse(option.data);
                if (parsedData.tr4) {
                  tr4 = parsedData.tr4;
                }
              } catch (e) { }
            }
            return {
              value: option.name,
              label: tr4 ? `${option.name} (${tr4})` : option.name,
            };
          })}
        />
      ) : null}
      {/* <div className="flex gap-1 w-full">
        {!values.hasTr4InOption && (
          <div className="w-1/2">
            <Label className={`my-0 mt-4`}>Cod. TR4</Label>
            <input
              value={values.tr4}
              onChange={handleChangeInp}
              className="w-full p-2 rounded-md"
              type="text"
              name="tr4"
            />
          </div>
        )}
        <div className="w-1/2">
          <Label className={`my-0 mt-4`}>Cod. del proveedor</Label>
          <input
            value={values.providerCode}
            onChange={handleChangeInp}
            className="w-full p-2 rounded-md"
            type="text"
            name="providerCode"
          />
        </div>
      </div> */}
      <div className="flex gap-1 items-end">
        <div className="">
          <Label className={`my-0 mt-4`}>Unidad</Label>
          <input
            value={values.unit}
            onChange={handleChangeInp}
            className="w-full p-2 rounded-md"
            type="text"
            name="unit"
          />
        </div>{" "}
        <div className="">
          <Label className={`my-0 mt-4`}>Cantidad</Label>
          <input
            value={values.amount}
            onChange={handleChangeInp}
            className="w-full p-2 rounded-md"
            type="text"
            name="amount"
          />
        </div>{" "}
        <div className="">
          <Label className={`my-0 mt-4`}>Obs.</Label>
          <input
            value={values.desc}
            onChange={handleChangeInp}
            className="w-full p-2 rounded-md"
            type="text"
            name="desc"
          />
        </div>
        <Button onClick={handleSubmit} className="px-3 py-2">
          Guardar
        </Button>
      </div>
    </form>
  );
};

const FormImportExcel = ({ field, reload, onClose, callDocument, options, existingData }) => {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const rows = validateArray(existingData)
        ? existingData.map((item) => {
          let values = null;
          if (item.description2) {
            try {
              values = JSON.parse(item.description2);
            } catch (e) {
              console.error("Error parsing description2", e);
            }
          }
          return {
            Detalle: item.label || "",
            Unidad: values?.unit || "",
            Cantidad: values?.amount || "",
            Obs: values?.desc || "",
            TR4: values?.tr4 || "",
            CodProveedor: values?.providerCode || "",
          };
        })
        : [
          {
            Detalle: "",
            Unidad: "",
            Cantidad: "",
            Obs: "",
            TR4: "",
            CodProveedor: "",
          },
        ];

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Plantilla");
      XLSX.writeFile(workbook, "Plantilla_SubData.xlsx");
    } catch (error) {
      toast.error("Error al generar la plantilla");
    }
  };

  const handleProcess = async () => {
    if (!file) return toast.error("Seleccione un archivo");
    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (!jsonData.length) {
            toast.warning("El archivo está vacío");
            setLoading(false);
            return;
          }

          const existingOptions = validateArray(options)
            ? options.map((o) => o.name)
            : [];
          const createdInBatch = new Set();
          let newOptionsCreated = false;

          for (const row of jsonData) {
            const itemName = row.Detalle || row.detalle || "";

            if (
              itemName &&
              !existingOptions.includes(itemName) &&
              !createdInBatch.has(itemName)
            ) {
              await requestAuth("post", "/Dropdown/Option", {
                Name: itemName,
                DropdownListLabel: field.urlInitial,
                data: JSON.stringify({ tr4: row.TR4 || row.tr4 || "" }),
              }).catch((err) => console.error("Error creating option:", err));
              createdInBatch.add(itemName);
              newOptionsCreated = true;
            }

            const payload = {
              Label: itemName,
              NumberValue: 0,
              Description: "",
              Description2: JSON.stringify({
                unit: row.Unidad || row.unidad || "",
                amount: row.Cantidad || row.cantidad || 0,
                desc: row.Obs || row.obs || "",
                tr4: row.TR4 || row.tr4 || "",
                providerCode: row.CodProveedor || row.codproveedor || "",
              }),
            };

            await requestAuth(
              "post",
              `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
              payload,
            ).catch((err) => console.error("Error al registrar fila:", err));
          }

          if (newOptionsCreated && callDocument) {
            await callDocument();
          }

          toast.success("Importación completada");
          reload();
          onClose();
        } catch (err) {
          toast.error("Error al procesar los datos del archivo");
          setLoading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      toast.error("Error al procesar el archivo");
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6 bg-white rounded-lg shadow-xl max-w-md mx-auto">
      <div className="flex justify-between items-center border-b pb-3">
        <H3 className="text-gray-800 font-bold">Importar desde Excel</H3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="space-y-6 py-4">
        <div className="bg-blue-50 p-4 rounded-md border border-blue-100 font-normal">
          <p className="text-sm text-blue-800 mb-3">
            1. Use la plantilla para asegurar el formato correcto de las
            columnas.
          </p>
          <Button
            onClick={handleDownloadTemplate}
            className="w-full sm:w-fit text-sm bg-blue-600 hover:bg-blue-700 py-2 px-4 rounded transition-all"
          >
            <i className="fas fa-file-download mr-2"></i> Descargar Plantilla
          </Button>
        </div>

        <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
          <p className="text-sm text-gray-700 mb-3 font-normal">
            2. Seleccione el archivo Excel (.xlsx o .xls)
          </p>
          <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors bg-white">
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={(e) => setFile(e.target.files[0])}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              id="excel-upload"
            />
            <div className="text-center">
              <i className="fas fa-cloud-upload-alt text-3xl text-gray-300 mb-2"></i>
              <p className="text-sm text-gray-500 truncate">
                {file ? file.name : "Haga clic o arrastre un archivo aquí"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t mt-2">
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-500 hover:text-gray-700 font-medium transition-colors"
        >
          Cancelar
        </button>
        <Button
          onClick={handleProcess}
          disabled={loading || !file}
          className={`${loading || !file ? "opacity-50 cursor-not-allowed" : ""} px-6 py-2 shadow-sm font-semibold`}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i> Procesando...
            </>
          ) : (
            "Iniciar Carga"
          )}
        </Button>
      </div>
    </div>
  );
};

export default InputSubData;
